import React from "react";
import Portal from "./Portal";
import useAudioVisualizer from "./AudioPlayer";

type RecordingState = "inactive" | "recording" | "paused";

type AudioRecorderPanelProps = {
  headerText: React.ReactNode;
  recordingState: RecordingState;
  recordingTime: number;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onAbort: () => void;
  startLabel: string;
  stopLabel: string;
  deleteLabel: string;
  durationLabel: React.ReactNode;
  limitWarning?: React.ReactNode;
  startIcon: React.ReactNode;
  startButtonClassName: string;
  stopButtonClassName: string;
  volumeBarRef: React.MutableRefObject<HTMLCanvasElement | null>;
  recordingStateRef: React.MutableRefObject<RecordingState>;
  analyserRef: React.MutableRefObject<AnalyserNode | null>;
  dataArrayRef: React.MutableRefObject<Uint8Array | null>;
  overlayClassName?: string;
  headerOverlayClassName?: string;
  headerClassName?: string;
  showHeaderOverlay?: boolean;
  headerOverlayTargetId?: string;
};

const AudioRecorderPanel: React.FC<AudioRecorderPanelProps> = ({
  headerText,
  recordingState,
  recordingTime,
  onStart,
  onPause,
  onResume,
  onStop,
  onAbort,
  startLabel,
  stopLabel,
  deleteLabel,
  durationLabel,
  limitWarning,
  startIcon,
  startButtonClassName,
  stopButtonClassName,
  volumeBarRef,
  recordingStateRef,
  analyserRef,
  dataArrayRef,
  overlayClassName = "fixed inset-0 bg-black bg-opacity-15 z-2",
  headerOverlayClassName = "fixed top-0 left-0 right-0 bg-black bg-opacity-15 z-150",
  headerClassName = "tw-mb-1 tw-gap-1 tw-flex tw-flex-row tw-justify-center",
  showHeaderOverlay = true,
  headerOverlayTargetId = "header",
}) => {
  const isRecording = recordingState !== "inactive";
  useAudioVisualizer({
    recordingState,
    recordingStateRef,
    analyserRef,
    dataArrayRef,
    volumeBarRef,
  });

  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <>
      {isRecording && <div className={overlayClassName} />}
      {isRecording && showHeaderOverlay && (
        <Portal>
          <div
            id="header-overlay"
            className={headerOverlayClassName}
            style={{
              height: `${
                document
                  .getElementById(headerOverlayTargetId)
                  ?.getBoundingClientRect().height ?? 65
              }px`,
            }}
          />
        </Portal>
      )}

      <div
        data-state="active"
        data-orientation="horizontal"
        role="tabpanel"
        aria-labelledby="radix-113-trigger-recording"
        id="radix-113-content-recording"
        tabIndex={0}
      >
        <div className="tw-flex tw-h-full tw-flex-col">
          <div className={headerClassName}>
            {headerText}
            <div className="lg:tw-hidden">
              {/*<div><img src="Record%20session_files/info-circle-a5f42b35.svg" alt="info icon" data-state="closed" /></div>*/}
            </div>
          </div>
          <div className="tw-flex-1 tw-flex-unset tw-flex tw-flex-col tw-justify-center">
            {recordingState === "inactive" ? (
              <div className="recorder__start-wrapper tw-flex tw-flex-col tw-items-center">
                <div className="tw-grid">
                  <button onClick={onStart} className={startButtonClassName}>
                    <div className="_btnFlex_19rto_32">
                      <div className="_btnAddon_19rto_56 _btnLeftAddon_19rto_61">
                        {startIcon}
                      </div>
                      {startLabel}
                    </div>
                  </button>
                </div>
                <div className="tw-flex tw-justify-center tw-items-center tw-gap-2 tw-mt-2">
                  <img className="tw-w-4" src="/dsgvo.png" alt="" />
                  <span>100% DSGVO konform</span>
                  <div>
                    <button className="tw-flex" data-state="closed">
                      <svg
                        width="16"
                        height="17"
                        viewBox="0 0 16 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.00033 1.32487C11.9537 1.32487 15.167 4.5382 15.167 8.49154C15.167 12.4449 11.9537 15.6582 8.00033 15.6582C4.04699 15.6582 0.833658 12.4449 0.833658 8.49154C0.833658 4.5382 4.04699 1.32487 8.00033 1.32487ZM8.00033 14.6582C11.4003 14.6582 14.167 11.8915 14.167 8.49154C14.167 5.09154 11.4003 2.32487 8.00033 2.32487C4.60033 2.32487 1.83366 5.09154 1.83366 8.49154C1.83366 11.8915 4.60033 14.6582 8.00033 14.6582Z"
                          fill="#8F8F8F"
                        ></path>
                        <path
                          d="M8 7.32487C8.27333 7.32487 8.5 7.55154 8.5 7.82487V11.1582C8.5 11.4315 8.27333 11.6582 8 11.6582C7.72667 11.6582 7.5 11.4315 7.5 11.1582V7.82487C7.5 7.55154 7.72667 7.32487 8 7.32487Z"
                          fill="#8F8F8F"
                        ></path>
                        <path
                          d="M8.00033 5.15828C8.08699 5.15828 8.17366 5.17828 8.25366 5.21161C8.33366 5.24495 8.40699 5.29162 8.47366 5.35162C8.53366 5.41828 8.58033 5.48495 8.61366 5.57162C8.64699 5.65162 8.66699 5.73828 8.66699 5.82495C8.66699 5.91161 8.64699 5.99828 8.61366 6.07828C8.58033 6.15828 8.53366 6.23161 8.47366 6.29828C8.40699 6.35828 8.33366 6.40495 8.25366 6.43828C8.09366 6.50495 7.90699 6.50495 7.74699 6.43828C7.66699 6.40495 7.59366 6.35828 7.52699 6.29828C7.46699 6.23161 7.42033 6.15828 7.38699 6.07828C7.35366 5.99828 7.33366 5.91161 7.33366 5.82495C7.33366 5.73828 7.35366 5.65162 7.38699 5.57162C7.42033 5.48495 7.46699 5.41828 7.52699 5.35162C7.59366 5.29162 7.66699 5.24495 7.74699 5.21161C7.82699 5.17828 7.91366 5.15828 8.00033 5.15828Z"
                          fill="#8F8F8F"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="tw-max-w-[600px] tw-mx-auto tw-relative">
                <svg
                  onClick={onAbort}
                  className="tw-absolute tw-right-0 tw-top-0 tw-cursor-pointer tw-bg-white tw-fill-white tw-w-[20px] tw-h-[20px]"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.00004 14.6666C11.6667 14.6666 14.6667 11.6666 14.6667 7.99992C14.6667 4.33325 11.6667 1.33325 8.00004 1.33325C4.33337 1.33325 1.33337 4.33325 1.33337 7.99992C1.33337 11.6666 4.33337 14.6666 8.00004 14.6666Z"
                    stroke="#32768D"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.11328 9.88661L9.88661 6.11328"
                    stroke="#32768D"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.88661 9.88661L6.11328 6.11328"
                    stroke="#32768D"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="_visualizer_o5yw5_1">
                  <div className="_canvasWrapper_o5yw5_28">
                    <canvas
                      ref={volumeBarRef}
                      height="70"
                      style={{ width: "100%", height: "100%" }}
                      width="419"
                    ></canvas>
                  </div>
                  <div className="_secondsWrapper_o5yw5_34">
                    {formatTime(recordingTime)}
                  </div>
                </div>
                <div className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-2 tw-mb-2">
                  <div className="tw-flex-1 bg-white rounded-[5px]">
                    <button
                      onClick={onAbort}
                      className="_button_19rto_40 _secondary_19rto_163 _filled_19rto_80 _block_19rto_52 recorder__delete-btn"
                    >
                      <div className="_btnFlex_19rto_32">{deleteLabel}</div>
                    </button>
                  </div>
                  <div className="tw-flex-1 bg-white rounded-[5px]">
                    {recordingState === "recording" ? (
                      <button
                        onClick={onPause}
                        className="_button_19rto_40 _primary_19rto_127 _outlined_19rto_69 _block_19rto_52 recorder__pause-btn !tw-font-normal"
                      >
                        <div className="_btnFlex_19rto_32">Pause</div>
                      </button>
                    ) : (
                      <button
                        onClick={onResume}
                        className="_button_19rto_40 _primary_19rto_127 _outlined_19rto_69 _block_19rto_52 recorder__pause-btn !tw-font-normal"
                      >
                        <div className="_btnFlex_19rto_32">Weiter</div>
                      </button>
                    )}
                  </div>
                  <div className="tw-flex-1">
                    <button onClick={onStop} className={stopButtonClassName}>
                      <div className="_btnFlex_19rto_32">{stopLabel}</div>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center lg:tw-mt-4 tw-mt-1">
            <div className="tw-flex tw-gap-2 tw-items-center">{durationLabel}</div>
            {recordingState !== "inactive" && limitWarning && (
              <div className="tw-text-xs tw-text-yellow-600 tw-mt-1 text-center">
                {limitWarning}
              </div>
            )}
            {recordingState !== "inactive" && (
              <p className="tw-text-[red] tw-m-0">
                Aktuelle Aufnahme: {Math.floor(recordingTime / 60)}min{" "}
                {recordingTime % 60}sec
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AudioRecorderPanel;
